// modifiedDutyApprovals.ts — pure helpers for the WCDP Modified Duty second approver
// (TAS-679). HR (the Modified Duty approver) must approve a Modified Duty employee's
// timesheet IN ADDITION to the supervisor; both are required, in any order.
//
// Tenant isolation: every helper here keys off fields only the WCDP API sends while its
// gate is on (`requiredSupers` with 2+ slots, `superSlotsTotal >= 2`, `waiting === true`,
// a non-empty `approvalChanges`). With those absent each helper returns null / false / '',
// so other tenants render exactly what they did before. Deliberately separate from
// slotApprovals.ts, which drives the NPM `allowManagerApproval` slot UI.

import type { AdminEmployeeRole } from '../interfaces/EmployeeInfoInterface';
import type { RequiredSuperSlot } from '../interfaces/EmployeeInterface';
import type { SupervisorsEmployee } from '../interfaces/SupervisorInterface';
import type { ApproveResult } from '../api/timesheet';

/** The WCDP multi-slot payload, or null when the timesheet has fewer than 2 slots. */
export function multiSlotSupers(requiredSupers: RequiredSuperSlot[] | null | undefined): RequiredSuperSlot[] | null {
  return Array.isArray(requiredSupers) && requiredSupers.length >= 2 ? requiredSupers : null;
}

export interface SuperSlotProgress {
  approved: number;
  total: number;
  pendingNames: string[];
}

/** Filled / total / still-open names for a multi-slot timesheet. */
export function superSlotProgress(slots: RequiredSuperSlot[]): SuperSlotProgress {
  const pending = slots.filter((s) => !s.approved);
  return {
    approved: slots.length - pending.length,
    total: slots.length,
    pendingNames: pending.map((s) => s.name).filter(Boolean),
  };
}

/** "Supervisor approvals 1 of 2 · waiting on Adelina Estrada" (or "… 2 of 2 complete"). */
export function superSlotSummaryText(p: SuperSlotProgress): string {
  const head = `Supervisor approvals ${p.approved} of ${p.total}`;
  if (p.approved >= p.total) return `${head} complete`;
  return p.pendingNames.length > 0 ? `${head} · waiting on ${p.pendingNames.join(', ')}` : head;
}

/**
 * The board-row waiting chip, from the Submitted-bucket row fields. Null unless the row
 * carries 2+ slots (WCDP gate on), so every other row and tenant renders nothing.
 */
export function boardSuperSlotChip(
  e: Pick<SupervisorsEmployee, 'superSlotsApproved' | 'superSlotsTotal' | 'pendingSuperNames'>
): { text: string; title: string; complete: boolean } | null {
  const total = e.superSlotsTotal;
  const done = e.superSlotsApproved;
  if (typeof total !== 'number' || total < 2 || typeof done !== 'number') return null;
  const names = (e.pendingSuperNames ?? []).filter(Boolean);
  const complete = done >= total;
  if (complete) {
    return { text: `${done} of ${total} approvals`, title: 'Every required supervisor approval is complete', complete };
  }
  const who = names.length > 0 ? names.join(', ') : 'another approver';
  return {
    text: done > 0 ? `${done} of ${total} · waiting on ${who}` : `${done} of ${total} approvals`,
    title: `Supervisor approvals ${done} of ${total} — waiting on ${who}. Both are required (Modified Duty).`,
    complete,
  };
}

/** Roles the API lets approve HR's slot on her behalf: admin || payroll || payrollApproveTimesheets. */
export function canApproveModifiedDutyOnBehalf(role: AdminEmployeeRole | null | undefined): boolean {
  if (!role) return false;
  return role.admin === true || role.payroll === true || role.payrollApproveTimesheets === true;
}

/** The unapproved Modified Duty (HR) slot that "Approve on behalf of …" targets, or null. */
export function onBehalfModifiedDutySlot(slots: RequiredSuperSlot[] | null | undefined): RequiredSuperSlot | null {
  return multiSlotSupers(slots)?.find((s) => s.isModifiedDutyApprover && !s.approved) ?? null;
}

/** True when /supervisor/approve filled a slot but the timesheet is still waiting on another. */
export function isWaitingOnAnotherApprover(res: ApproveResult | null | undefined): boolean {
  return res?.waiting === true;
}

/** Toast copy for a `waiting` response. `onBehalfOf` names the slot owner when acting for them. */
export function waitingApprovalText(res: ApproveResult, onBehalfOf?: string): string {
  const names = (res.pendingSuperNames ?? []).filter(Boolean);
  const who = names.length > 0 ? names.join(', ') : 'another approver';
  const lead = res.alreadyApproved
    ? 'Your part was already approved'
    : onBehalfOf ? `Approved on behalf of ${onBehalfOf}` : 'Approved your part';
  return `${lead} · waiting on ${who}`;
}

// ── Modified Duty page: approvalChanges toast ─────────────────────────────────

export type ApprovalChangeAction = 'added' | 'returned' | 'removed' | 'advanced' | 'skippedExported' | 'error';

export interface ApprovalChange {
  payPeriodID: number;
  weekNumber: number;
  timesheetID: number;
  action: ApprovalChangeAction;
  message?: string;
}

const ACTION_TEXT: Record<ApprovalChangeAction, string> = {
  added: 'added to Modified Duty approval',
  returned: 'returned for Modified Duty approval',
  removed: 'Modified Duty approval removed',
  advanced: 'Modified Duty approval removed — moved to Payroll Pending',
  skippedExported: 'skipped (payroll already exported)',
  error: 'could not update approvals',
};

/**
 * "Week 42: returned for Modified Duty approval · Week 41: skipped (payroll already exported)".
 * '' for an empty / missing list (every non-WCDP tenant), so the caller shows nothing extra.
 * Ordered newest week first.
 */
export function summarizeApprovalChanges(changes: ApprovalChange[] | null | undefined): string {
  if (!Array.isArray(changes) || changes.length === 0) return '';
  return [...changes]
    .sort((a, b) => b.weekNumber - a.weekNumber)
    .map((c) => {
      const text = ACTION_TEXT[c.action] ?? c.action;
      const detail = c.action === 'error' && c.message ? ` (${c.message})` : '';
      return `Week ${c.weekNumber}: ${text}${detail}`;
    })
    .join(' · ');
}

/** True when any change needs the user's attention (a skip or an error). */
export function approvalChangesNeedAttention(changes: ApprovalChange[] | null | undefined): boolean {
  return Array.isArray(changes) && changes.some((c) => c.action === 'skippedExported' || c.action === 'error');
}
